export const VGA_WIDTH = 640;
export const FULL_FOV_WIDTH = 864;
export const STREAM_HEIGHT = 480;
const FLOAT_THRESHOLD = 0.01;
const ASPECT_RATIO_2X1 = 2 * FULL_FOV_WIDTH / STREAM_HEIGHT;

export type streamLocation = [left: number, top: number, width: number, height: number];

export enum VideoStreamType {
    InCabinCamera = 'InCabinCamera',
    FrontFacingCamera = 'FrontFacingCamera',
    BlindSpotCamera = 'BlindSpotCamera',
    RightBlindSpotCamera = 'RightBlindSpotCamera',
    LeftBlindSpotCamera = 'LeftBlindSpotCamera',
    RearCamera = 'RearCamera',
}

export const VIDEO_CAMERA_ORDER = [
    VideoStreamType.InCabinCamera,
    VideoStreamType.FrontFacingCamera,
    VideoStreamType.RightBlindSpotCamera,
    VideoStreamType.LeftBlindSpotCamera,
    VideoStreamType.BlindSpotCamera,
    VideoStreamType.RearCamera,
];

export const cameraToVideoStreamType = (camera: string): VideoStreamType => {
    switch (camera) {
        case 'front_facing':
            return VideoStreamType.FrontFacingCamera;
        case 'in_cabin':
            return VideoStreamType.InCabinCamera;
        case 'bsd_left':
            return VideoStreamType.LeftBlindSpotCamera;
        case 'bsd_right':
            return VideoStreamType.RightBlindSpotCamera;
        case 'rear_facing':
            return VideoStreamType.RearCamera;
        default:
            return VideoStreamType.InCabinCamera;
    }
}

export const DEFAULT_STREAMS: Record<VideoStreamType, streamLocation> = {
    [VideoStreamType.InCabinCamera]: [0, 0, VGA_WIDTH, STREAM_HEIGHT],
    [VideoStreamType.FrontFacingCamera]: [FULL_FOV_WIDTH, 0, FULL_FOV_WIDTH, STREAM_HEIGHT],
    [VideoStreamType.BlindSpotCamera]: [0, STREAM_HEIGHT, FULL_FOV_WIDTH, STREAM_HEIGHT],
    [VideoStreamType.RightBlindSpotCamera]: [0, STREAM_HEIGHT, FULL_FOV_WIDTH, STREAM_HEIGHT],
    [VideoStreamType.LeftBlindSpotCamera]: [FULL_FOV_WIDTH, STREAM_HEIGHT, FULL_FOV_WIDTH, STREAM_HEIGHT],
    [VideoStreamType.RearCamera]: [FULL_FOV_WIDTH, STREAM_HEIGHT, FULL_FOV_WIDTH, STREAM_HEIGHT],
};

export const detectStreamCountWithScale = (origWidth: number, origHeight: number, scale: number): number => {
    const videoWidth = Math.floor(origWidth * scale);
    const videoHeight = Math.floor(origHeight * scale);

    // Single stream: height === STREAM_HEIGHT && width is VGA_WIDTH or FULL_FOV_WIDTH
    if (videoHeight === STREAM_HEIGHT && [VGA_WIDTH, FULL_FOV_WIDTH].includes(videoWidth)) {
        return 1;
    }

    // Two streams: height === STREAM_HEIGHT && width is 2*VGA_WIDTH, 2*FULL_FOV_WIDTH, or VGA_WIDTH + FULL_FOV_WIDTH
    if (
        videoHeight === STREAM_HEIGHT &&
        [2 * VGA_WIDTH, 2 * FULL_FOV_WIDTH, VGA_WIDTH + FULL_FOV_WIDTH].includes(videoWidth)
    ) {
        return 2;
    }

    // Two streams - both FULL_FOV_WIDTH (aspect ratio ≈ 32/9)
    if (Math.abs(videoWidth / videoHeight - ASPECT_RATIO_2X1) < FLOAT_THRESHOLD) {
        return 2;
    }

    // Three streams: height === 3 * STREAM_HEIGHT && width is VGA_WIDTH or FULL_FOV_WIDTH
    if (videoHeight === 3 * STREAM_HEIGHT && [VGA_WIDTH, FULL_FOV_WIDTH].includes(videoWidth)) {
        return 3;
    }

    // Four streams: height === 2 * STREAM_HEIGHT && width is VGA_WIDTH or FULL_FOV_WIDTH
    if (videoHeight === 2 * STREAM_HEIGHT && [2 * VGA_WIDTH, 2 * FULL_FOV_WIDTH].includes(videoWidth)) {
        return 4;
    }

    return 0;
};

export const geussStream = (stream: VideoStreamType, videoWidth: number, videoHeight: number): streamLocation => {
    let res: streamLocation | null = null;
    res = geussStreamWithScale(stream, videoWidth, videoHeight, 1);
    if (res !== null) {
        return res;
    }

    res = geussStreamWithScale(stream, videoWidth, videoHeight, 2);
    if (res !== null) {
        return res.map((value) => Math.floor(value / 2)) as streamLocation;
    }
    return DEFAULT_STREAMS[stream];
};

const geussStreamWithScale = (
    stream: VideoStreamType,
    origVideoWidth: number,
    origVideoHeight: number,
    scale: number,
): streamLocation | null => {
    const videoWidth = Math.floor(origVideoWidth * scale);
    const videoHeight = Math.floor(origVideoHeight * scale);
    
    // Use detectStreamCountWithScale to determine stream count
    const streamCount = detectStreamCountWithScale(origVideoWidth, origVideoHeight, scale);
    
    if (streamCount === 1) {
        // single stream
        return [0, 0, videoWidth, videoHeight];
    }
    
    if (streamCount === 2) {
        // Check if it's the 32:9 aspect ratio case (both FULL_FOV_WIDTH)
        if (Math.abs(videoWidth / videoHeight - ASPECT_RATIO_2X1) < FLOAT_THRESHOLD) {
            // two streams - both FULL_FOV_WIDTH
            if (stream === VideoStreamType.InCabinCamera) {
                return [0, 0, videoWidth / 2, videoHeight];
            }
            return [videoWidth / 2, 0, videoWidth / 2, videoHeight];
        }
        // Standard two streams layout
        if (stream === VideoStreamType.InCabinCamera) {
            return DEFAULT_STREAMS[stream];
        }
        return [VGA_WIDTH, 0, FULL_FOV_WIDTH, STREAM_HEIGHT];
    }
    
    if (streamCount === 3) {
        // three streams
        if (stream === VideoStreamType.InCabinCamera) {
            return DEFAULT_STREAMS[stream];
        } else if (stream === VideoStreamType.FrontFacingCamera) {
            return [0, STREAM_HEIGHT, FULL_FOV_WIDTH, STREAM_HEIGHT];
        } else {
            return [0, 2 * STREAM_HEIGHT, FULL_FOV_WIDTH, STREAM_HEIGHT];
        }
    }
    
    return null;
};

/**
 * Detects the number of streams in an image based on its dimensions
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @returns Number of streams detected (1, 2, 3, 4, or 0 if unknown)
 */
export const detectStreamCount = (width: number, height: number): number => {
    // Try with scale 1 first
    const count1 = detectStreamCountWithScale(width, height, 1);
    if (count1 > 0) {
        return count1;
    }

    // Try with scale 2
    const count2 = detectStreamCountWithScale(width, height, 2);
    if (count2 > 0) {
        return count2;
    }

    // Default to 2 streams if cannot determine (backward compatibility)
    return 2;
};

